import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clientApi } from '@/config/axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DocumentPreview } from '@/features/emr';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { type Demographics, IntakeReview, type Match, ReprocessSource } from './IntakeReview';
import { ProvisionalReview } from '@/features/assistant/ProvisionalReview';

export type Detail = {
  processing_enabled?: boolean;
  provisional?: {
    patient_id: string | null;
    status: string;
    created_patient: boolean;
    patient_is_provisional?: boolean;
    patient_label?: string | null;
    version: number;
  } | null;
  id: string;
  title: string;
  status: string;
  reason: string | null;
  source_text: string;
  revision_id?: string;
  can_reprocess?: boolean;
  intake_patient_id?: string | null;
  assigned_patient?: { patient_id: string; label: string } | null;
  duplicates?: { job_id: string; title: string; kind: string }[];
  payload: null | {
    labs?: {
      test_name: string;
      value: string;
      unit: string;
      collected_date?: string;
      reported_date?: string;
      reference_range?: string;
      page: number;
    }[];
    classification: string;
    classification_verified?: boolean;
    identity_clear?: boolean;
    demographics?: Demographics;
    issues?: {
      field: string | null;
      reason: string;
      value?: string;
      quote?: string;
      page?: number;
      quote_verified?: boolean;
    }[];
    classification_quote: string;
    facts: Fact[];
    checklist: { key: string; label: string; status: string; evidence: Fact[] }[];
    summary: { heading: string; facts: Fact[] }[];
    patient_matches: Match[];
  };
};
type Fact = { field: string; value: string; quote: string; page: number; start: number; end: number };
export function reasonLabel(reason: string) {
  return (
    (
      {
        model_access_denied: 'The model service denied access.',
        model_incomplete_or_refused: 'The model did not return a complete response.',
        evidence_page_invalid: 'The cited page does not exist in the saved extraction.',
        extraction_failed: 'Processing failed before a review proposal could be saved.',
        fields_require_review: 'Some fields need review. Validated fields are retained below.',
        statement_must_preserve_context: 'The extracted statement changed or omitted source context.',
        conflicting_patient_identifiers: 'Patient identifiers conflict; clarify identity before intake.',
        classification_evidence_invalid: 'The referral classification has no verified supporting quotation.',
        evidence_ambiguous_or_invalid: 'The quotation is missing or appears more than once on the cited page.',
        field_context_missing:
          'The value could not be verified together with its field label. Other validated fields remain available.',
        patient_scope_requires_review:
          'The model could not establish one patient identity for this packet. This is not proof that multiple patients are present.',
      } as Record<string, string>
    )[reason] || reason.replaceAll('_', ' ')
  );
}

export function ReferralDetail({ detail, processingEnabled = true }: { detail: Detail; processingEnabled?: boolean }) {
  const [selected, setSelected] = useState<Fact | null>(null);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{detail.title}</CardTitle>
        <CardDescription>Development proposal — not part of the chart</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <Badge variant="secondary">{statusLabel(detail.status)}</Badge>
        {detail.reason && <p>{reasonLabel(detail.reason)}</p>}
        {detail.can_reprocess && detail.revision_id && (
          <ReprocessSource revisionId={detail.revision_id} enabled={processingEnabled} />
        )}
        {!!detail.duplicates?.length && (
          <Alert>
            <AlertTitle>Possible duplicate</AlertTitle>
            <AlertDescription>
              {detail.duplicates.map((d) => d.title).join(', ')} has substantially similar extracted text. Review the
              existing intake before creating another patient.
            </AlertDescription>
          </Alert>
        )}
        {detail.provisional && <ProvisionalReview detail={detail} />}
        {detail.payload && (
          <>
            <p>Classification: {detail.payload.classification.replaceAll('_', ' ')}</p>
            <blockquote className="border-l-2 pl-4 whitespace-pre-wrap">
              {detail.payload.classification_quote}
            </blockquote>
            {!!detail.payload.issues?.length && (
              <Collapsible>
                <CollapsibleTrigger asChild>
                  <Button variant="outline">Items needing review ({detail.payload.issues.length})</Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="flex flex-col gap-3 pt-3">
                  {detail.payload.issues.map((issue, i) => (
                    <Alert key={i}>
                      <AlertTitle>{issue.field?.replaceAll('_', ' ') || 'Document review'}</AlertTitle>
                      <AlertDescription>
                        <p>{reasonLabel(issue.reason)}</p>
                        {issue.value && <p>Unaccepted value: {issue.value}</p>}
                        {issue.quote && <blockquote className="whitespace-pre-wrap">{issue.quote}</blockquote>}
                        {issue.page && (
                          <p>
                            Reported page {issue.page} ·{' '}
                            {issue.quote_verified
                              ? 'Quotation found in source; field association needs review'
                              : 'Quotation not verified; compare with original'}
                          </p>
                        )}
                      </AlertDescription>
                    </Alert>
                  ))}
                </CollapsibleContent>
              </Collapsible>
            )}
            <Collapsible>
              <CollapsibleTrigger asChild>
                <Button variant="outline">Extracted referral fields ({detail.payload.facts.length})</Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="flex flex-col gap-3 pt-3" aria-label="Extracted referral fields">
                {detail.payload.facts.map((fact, i) => (
                  <div key={i} className="grid gap-2 sm:grid-cols-[1fr_2fr_auto]">
                    <span>{fact.field.replaceAll('_', ' ')}</span>
                    <span className="whitespace-pre-wrap">{fact.value}</span>
                    <Button size="sm" variant="outline" onClick={() => setSelected(fact)}>
                      Source page {fact.page}
                    </Button>
                  </div>
                ))}
              </CollapsibleContent>
            </Collapsible>
            {selected && (
              <Alert>
                <AlertTitle>Source evidence — page {selected.page}</AlertTitle>
                <AlertDescription>
                  <blockquote className="whitespace-pre-wrap">{selected.quote}</blockquote>
                </AlertDescription>
              </Alert>
            )}
            {!!detail.payload.labs?.length && (
              <section className="flex flex-col gap-3" aria-label="Recognized laboratory results">
                <h2>Laboratory results</h2>
                <p>Imported to the maternal flowsheet when intake is saved. Missing dates remain undated.</p>
                {detail.payload.labs.map((lab, index) => (
                  <div key={index}>
                    <strong>{lab.test_name}</strong>: {lab.value} {lab.unit}
                    <p>
                      {lab.collected_date || lab.reported_date || 'Undated'} · Source page {lab.page}
                      {lab.reference_range && ` · Range: ${lab.reference_range}`}
                    </p>
                  </div>
                ))}
              </section>
            )}
            {detail.payload.demographics && (
              <IntakeReview
                jobId={detail.id}
                applied={detail.intake_patient_id}
                provisional={detail.provisional}
                assignedPatient={detail.assigned_patient}
                matches={detail.payload.patient_matches}
                demographics={detail.payload.demographics}
                identityClear={!!detail.payload.identity_clear}
                referral={detail.payload.classification === 'referral' && !!detail.payload.classification_verified}
              />
            )}
            <Collapsible>
              <CollapsibleTrigger asChild>
                <Button variant="outline">Referral checklist</Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3">
                {!detail.payload.checklist.length && <p>No applicable practice checklist has been published.</p>}
                {detail.payload.checklist.map((item) => (
                  <p key={item.key}>
                    {item.label}: {item.status}{' '}
                    {item.evidence.length
                      ? '(source mentions this item; confirm receipt)'
                      : '(not established by extraction)'}
                  </p>
                ))}
              </CollapsibleContent>
            </Collapsible>
            <section aria-label="Pre-visit summary" className="flex flex-col gap-3">
              <h2>Pre-visit summary proposal</h2>
              {detail.payload.summary.map((section) => (
                <div key={section.heading}>
                  <h3>{section.heading}</h3>
                  {section.facts.length ? (
                    section.facts.map((fact, i) => (
                      <p key={i} className="whitespace-pre-wrap">
                        “{fact.value}” — page {fact.page}
                      </p>
                    ))
                  ) : (
                    <p>Not established from this source.</p>
                  )}
                </div>
              ))}
            </section>
          </>
        )}
        <details>
          <summary>Read complete source text</summary>
          <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words">{detail.source_text}</pre>
        </details>
      </CardContent>
      <CardFooter>
        <p>
          Processing alone does not change the chart. Confirmed intake links the document and updates the patient
          checklist, source history, missing demographics, and laboratory flowsheet; it does not send messages or sign
          reports.
        </p>
      </CardFooter>
    </Card>
  );
}

export function statusLabel(status: string) {
  return (
    (
      {
        running: 'Processing',
        not_referral: 'Records only',
        queued: 'Waiting to process',
        needs_human: 'Review details',
        ready: 'Ready for intake review',
      } as Record<string, string>
    )[status] || status.replaceAll('_', ' ')
  );
}
export function OriginalSource({ base, jobId }: { base: string; jobId: string }) {
  const source = useQuery({
    gcTime: 0,
    refetchInterval: 240000,
    queryKey: ['agent-source', base, jobId],
    queryFn: async () =>
      (
        await clientApi.get<{ file_url: string | null; file_name: string; content_type: string }>(
          `${base}/proposals/${jobId}/source`,
        )
      ).data,
  });
  return (
    <Card className="min-h-0 xl:h-full xl:overflow-y-auto">
      <CardHeader>
        <CardTitle>Original source document</CardTitle>
      </CardHeader>
      <CardContent className="[&_iframe]:h-[calc(100dvh-15rem)]">
        {source.isError ? (
          <p role="alert">
            Source unavailable or changed.{' '}
            <Button variant="outline" onClick={() => source.refetch()}>
              Retry source
            </Button>
          </p>
        ) : source.isPending ? (
          <p role="status">Loading original…</p>
        ) : source.data.file_url ? (
          <DocumentPreview
            fileUrl={source.data.file_url}
            fileName={source.data.file_name}
            contentType={source.data.content_type}
          />
        ) : (
          <p>This example has no original file. Use the complete source text in the proposal.</p>
        )}
      </CardContent>
    </Card>
  );
}
